import { oc } from "@orpc/contract";
import { z } from "zod";
import { IntegrationBaseSchema, IntegrationKey } from "./base";

/**
 * Oracle Fusion Integration Settings Schema
 *
 * `password` is optional on updates — when omitted the backend preserves
 * the existing encrypted value. It is always required on first save.
 */
export const OracleFusionIntegrationSettingsSchema = z.object({
  baseUrl: z.string(),
  username: z.string(),
  password: z.string().optional(),
  apiVersion: z.string().optional(),
  organizationCode: z.string().optional(),
  itemsPerPage: z.number().optional(),
});

/**
 * Oracle Fusion Integration Settings Type
 */
export type OracleFusionIntegrationSettings = z.infer<
  typeof OracleFusionIntegrationSettingsSchema
>;

/**
 * Oracle Fusion integration variant (without base fields)
 */
export const OracleFusionIntegrationSettingsSchemaWithoutBase = z.object({
  key: z.literal(IntegrationKey.OracleFusion),
  settings: OracleFusionIntegrationSettingsSchema,
});

/**
 * Full Oracle Fusion integration schema (with base fields)
 */
export const OracleFusionIntegrationSchema = IntegrationBaseSchema.merge(
  OracleFusionIntegrationSettingsSchemaWithoutBase,
);

/**
 * Oracle Fusion Integration Type
 */
export type OracleFusionIntegration = z.infer<
  typeof OracleFusionIntegrationSchema
>;

// ── Sync state DTO ──

export const FusionSyncStateSchema = z.object({
  id: z.string().uuid(),
  integrationType: z.string(),
  lastSyncTime: z.coerce.date(),
  lastSyncStatus: z.enum(["success", "partial_failure", "failure"]),
  lastSyncRowCount: z.number(),
  updatedAt: z.coerce.date(),
});

export type FusionSyncStateDTO = z.infer<typeof FusionSyncStateSchema>;

// ── Contracts ──

const testConnection = oc
  .input(z.object({ integrationId: z.string().uuid() }))
  .output(
    z.object({
      success: z.boolean(),
      message: z.string(),
    }),
  );

const listSyncState = oc
  .input(z.object({ integrationId: z.string().uuid() }))
  .output(z.array(FusionSyncStateSchema))
  .route({ method: "GET" });

const resetSyncState = oc
  .input(
    z.object({
      integrationId: z.string().uuid(),
      integrationTypes: z.array(z.string()).min(1),
    }),
  )
  .output(z.object({ deleted: z.number() }));

export const oracleFusionContracts = {
  testConnection,
  listSyncState,
  resetSyncState,
} as const;

export type OracleFusionContracts = typeof oracleFusionContracts;
